import type { ServiceLanguage } from "@albertine/i18n";
import type { CatalogArticle, GooglePlace, OfferingCategory } from "@albertine/types";
import type { SearchCatalogItemsQuery } from "@albertine/types/openapi";
import { subMonths } from "date-fns";
import {
    type AddressComponents,
    type AddressFields,
    addressComponentsToObject,
    type CatalogItem,
    type CatalogItemEditables,
    type CatalogItemFormSchema,
    type CatalogItemTranslationDe,
    type CatalogItemTranslationFr,
    type CityGuideLocation,
    moreStrictLocationSearchThanCountry,
} from "../CatalogItem";
import type { ArticlePreview, ArticlePreviewPlace } from "../types/Storyblok";
import type { CatalogItemEditableTranslation } from "../types/Translation";
import { excludeUndefined } from "./array.util";
import { googlePlaceToAddressComponents } from "./google/GooglePlaceService";
import { findComponent } from "./google.util";
import { ifEmptyDefaultToNull, isEmpty } from "./string.util";
import { allowCategorySpecificTagsOnly } from "./tags.util";
import { getCatalogItemTranslationKey } from "./translation.util";

export function catalogItemToArticlePreview(
    catalogItem: CatalogItem | CatalogItemEditables,
): ArticlePreview {
    const {
        name,
        description,
        localInsights,
        images,
        geoPoint,
        address,
        googlePlaceId,
        keyDetail,
        headline,
        website,
        tags,
        benefits,
    } = catalogItem;
    const articleImages =
        images && images.length !== 0
            ? images.map((image) => ({
                  id: Number(image.id),
                  filename: image.url,
              }))
            : [];
    const place: Omit<ArticlePreviewPlace, "uid"> | undefined =
        geoPoint?.latitude && geoPoint.longitude && googlePlaceId
            ? {
                  address: address || "",
                  name: name || "",
                  lat: geoPoint.latitude.toString(),
                  lon: geoPoint.longitude.toString(),
                  googlePlaceId,
                  component: "googlePlace",
              }
            : undefined;

    const articlePreview: ArticlePreview = {
        title: name || "",
        content: description || "",
        coverImage: articleImages.length ? articleImages[0] : null,
        additionalImages: articleImages.length > 1 ? articleImages.slice(1) : [],
        insight: localInsights || undefined,
        component: "article",
        callToAction: null,
        place: place ? [place] : [],
        keyDetail: keyDetail || undefined,
        headline: headline || undefined,
        website: website || undefined,
        tags: tags || [],
        benefits: benefits || undefined,
    };

    return articlePreview;
}

type TranslationOverrides = {
    name: string;
};

export function translationToCatalogItemTranslationDe(
    translation: CatalogItemEditableTranslation,
    overrides?: TranslationOverrides,
): CatalogItemTranslationDe {
    const nameDe = overrides?.name
        ? overrides.name
        : ifEmptyDefaultToNull(translation.name);
    return {
        addressDe: ifEmptyDefaultToNull(translation.address),
        benefitsDe: ifEmptyDefaultToNull(translation.benefits),
        bookingPolicyDe: ifEmptyDefaultToNull(translation.bookingPolicy),
        descriptionDe: ifEmptyDefaultToNull(translation.description),
        eventDatesDe: ifEmptyDefaultToNull(translation.eventDates),
        headlineDe: ifEmptyDefaultToNull(translation.headline),
        localInsightsDe: ifEmptyDefaultToNull(translation.localInsights),
        travelItineraryNotesDe: ifEmptyDefaultToNull(translation.travelItineraryNotes),
        nameDe,
    };
}

export function translationToCatalogItemTranslationFr(
    translation: CatalogItemEditableTranslation,
    overrides?: TranslationOverrides,
): CatalogItemTranslationFr {
    const nameFr = overrides?.name
        ? overrides.name
        : ifEmptyDefaultToNull(translation.name);
    return {
        addressFr: ifEmptyDefaultToNull(translation.address),
        benefitsFr: ifEmptyDefaultToNull(translation.benefits),
        bookingPolicyFr: ifEmptyDefaultToNull(translation.bookingPolicy),
        descriptionFr: ifEmptyDefaultToNull(translation.description),
        eventDatesFr: ifEmptyDefaultToNull(translation.eventDates),
        headlineFr: ifEmptyDefaultToNull(translation.headline),
        localInsightsFr: ifEmptyDefaultToNull(translation.localInsights),
        travelItineraryNotesFr: ifEmptyDefaultToNull(translation.travelItineraryNotes),
        nameFr,
    };
}

export type TranslationToLangugePropertiesFunction =
    | typeof translationToCatalogItemTranslationDe
    | typeof translationToCatalogItemTranslationFr;

export function fromCatalogItemFormToEditables(
    catalogFormUpdate: CatalogItemFormSchema,
): CatalogItemEditables {
    // Destruct images from update because images are sent as asset references
    const { images, offeringCategory, tags, translations, ...rest } = catalogFormUpdate;

    const de = translations?.de
        ? translationToCatalogItemTranslationDe(translations?.de)
        : undefined;
    const fr = translations?.fr
        ? translationToCatalogItemTranslationFr(translations?.fr)
        : undefined;

    return {
        ...rest,
        benefits: ifEmptyDefaultToNull(rest.benefits),
        bookingPolicy: ifEmptyDefaultToNull(rest.bookingPolicy),
        name: rest.name,
        description: rest.description,
        discoverableForTenant: rest.discoverableForTenant ?? null,
        offeringCategories: [offeringCategory],
        headline: ifEmptyDefaultToNull(rest.headline),
        localInsights: ifEmptyDefaultToNull(rest.localInsights),
        eventDates: ifEmptyDefaultToNull(rest.eventDates),
        travelItineraryNotes: ifEmptyDefaultToNull(rest.travelItineraryNotes),
        notes: ifEmptyDefaultToNull(rest.notes),
        source: rest.source,
        address: ifEmptyDefaultToNull(rest.address),
        phoneNumber: ifEmptyDefaultToNull(rest.phoneNumber),
        website: ifEmptyDefaultToNull(rest.website),
        tags: tags ? allowCategorySpecificTagsOnly(tags, offeringCategory) : [],
        published: rest.published,
        addressComponents: rest.addressComponents || null,
        googlePlaceId: ifEmptyDefaultToNull(rest.googlePlaceId),
        googleUrl: ifEmptyDefaultToNull(rest.googleUrl),
        geoPoint: rest.geoPoint || null,
        ...de,
        ...fr,
    };
}

export function showRecentlyOpened(
    offeringCategories: OfferingCategory[] | null | undefined,
) {
    if (!offeringCategories) {
        return false;
    }
    const showRecentlyOpenedForCategories: OfferingCategory[] = [
        "dining",
        "nightlife",
        "accommodation",
        "localExperience",
    ];
    return offeringCategories.some((offeringCategory) =>
        showRecentlyOpenedForCategories.includes(offeringCategory),
    );
}

export function showAddress(offeringCategories: OfferingCategory[] | null | undefined) {
    if (!offeringCategories) {
        return true;
    }
    return !offeringCategories.includes("flight");
}

export function showEventDates(
    offeringCategories: OfferingCategory[] | null | undefined,
) {
    if (!offeringCategories) {
        return false;
    }
    return offeringCategories.includes("event");
}

export function showTravelItineraryNotes(
    offeringCategories: OfferingCategory[] | null | undefined,
) {
    if (!offeringCategories) {
        return false;
    }
    return offeringCategories.includes("travelItinerary");
}

export function isRecentlyOpened(recentlyOpened: string | null | undefined) {
    const recentlyOpenedDate = recentlyOpened && new Date(recentlyOpened);
    const recentlyOpenedWithin3M =
        recentlyOpenedDate && recentlyOpenedDate > subMonths(new Date(), 3);
    return !!recentlyOpenedWithin3M;
}

export function locationString(location: Pick<CatalogArticle, "location">) {
    const adminArea1 = !isEmpty(location.location?.administrativeAreaLevel1)
        ? location.location?.administrativeAreaLevel1
        : undefined;
    const adminArea2 = !isEmpty(location.location?.administrativeAreaLevel2)
        ? location.location?.administrativeAreaLevel2
        : undefined;
    const adminArea = adminArea1 || adminArea2;

    const locality = !isEmpty(location.location?.locality)
        ? location.location?.locality
        : undefined;

    const city = !isEmpty(location.location?.postalTown)
        ? location.location?.postalTown
        : locality;
    const country = !isEmpty(location.location?.country)
        ? location.location?.country
        : undefined;
    return [
        location.location?.neighborhood,
        location.location?.sublocalityLevel1,
        city,
        adminArea,
        country,
    ]
        .filter(excludeUndefined)
        .join(", ");
}

function toComponentMap(addressComponents: AddressComponents) {
    return {
        country: findComponent(addressComponents, "country")?.longName,
        countryCode: findComponent(addressComponents, "country")?.shortName,
        administrativeAreaLevel1: findComponent(
            addressComponents,
            "administrative_area_level_1",
        )?.longName,
        administrativeAreaLevel2: findComponent(
            addressComponents,
            "administrative_area_level_2",
        )?.longName,
        postalTown: findComponent(addressComponents, "postal_town")?.longName,
        locality: findComponent(addressComponents, "locality")?.longName,
        sublocalityLevel1: findComponent(addressComponents, "sublocality_level_1")
            ?.shortName,
    };
}

export function resolveCityCountryCountryCode(
    addressComponents?: AddressComponents,
): { city: string; country: string; countryCode: string } | null {
    if (!addressComponents) return null;
    const parsed = toComponentMap(addressComponents);
    const {
        country,
        countryCode,
        postalTown,
        locality,
        administrativeAreaLevel1,
        administrativeAreaLevel2,
        sublocalityLevel1,
    } = parsed;
    if (!country || !countryCode) return null;

    let city: string | undefined;

    switch (countryCode) {
        case "SG":
            city = "Singapore";
            break;
        case "HK":
            city = "Hong Kong";
            break;
        case "GB":
        case "SE":
            if (postalTown) {
                city = postalTown;
            }
            break;
        case "US":
            if (administrativeAreaLevel1 === "New York" && sublocalityLevel1) {
                if (
                    [
                        "Bronx",
                        "Brooklyn",
                        "Manhattan",
                        "Queens",
                        "Staten Island",
                    ].includes(sublocalityLevel1)
                ) {
                    city = "New York";
                }
                break;
            }
            if (administrativeAreaLevel2 === "Los Angeles County") {
                city = "Los Angeles";
            }
            if (administrativeAreaLevel2 === "Miami-Dade County") {
                city = "Miami";
            }
            break;

        case "JP":
            city = administrativeAreaLevel1;
            break;

        case "DE":
            if (locality === "München") {
                city = "Munich";
            }
            break;
        case "CH":
            if (locality === "Genève") {
                city = "Geneva";
            }
            break;
        case "IE":
            if (administrativeAreaLevel1 === "County Dublin") {
                city = "Dublin";
            }
            break;
        case "LU":
            city = "Luxembourg";
            break;
        case "MC":
            city = "Monaco";
            break;

        default:
    }

    if (city) {
        return { city, country, countryCode };
    }

    if (locality) {
        return { city: locality, country, countryCode };
    }

    if (!administrativeAreaLevel1) return null;

    return {
        city: administrativeAreaLevel1,
        country,
        countryCode,
    };
}

export const cityguideKeyDelimiter = "-";

export function cityGuideKey(input: { city: string; countryCode: string }): string {
    return `${input.city.replaceAll(" ", "_")}${cityguideKeyDelimiter}${
        input.countryCode
    }`.toLocaleLowerCase();
}

export function resolveCityGuideLocation(
    addressComponents?: AddressComponents,
): CityGuideLocation | null {
    const components = resolveCityCountryCountryCode(addressComponents);
    if (!components) return null;
    return {
        city: components.city,
        country: components.countryCode,
        key: cityGuideKey(components),
    };
}

export function getCatalogItemTranslations(
    catalogItem: CatalogItem | CatalogItemEditables | undefined,
    serviceLanguage: ServiceLanguage,
): CatalogItemEditableTranslation {
    const addressKey = getCatalogItemTranslationKey("address", serviceLanguage);
    const bookingPolicyKey = getCatalogItemTranslationKey(
        "bookingPolicy",
        serviceLanguage,
    );
    const benefitsKey = getCatalogItemTranslationKey("benefits", serviceLanguage);
    const descriptionKey = getCatalogItemTranslationKey("description", serviceLanguage);
    const eventDatesKey = getCatalogItemTranslationKey("eventDates", serviceLanguage);
    const headlineKey = getCatalogItemTranslationKey("headline", serviceLanguage);
    const localInsightsKey = getCatalogItemTranslationKey(
        "localInsights",
        serviceLanguage,
    );
    const nameKey = getCatalogItemTranslationKey("name", serviceLanguage);
    const travelItineraryNotesKey = getCatalogItemTranslationKey(
        "travelItineraryNotes",
        serviceLanguage,
    );

    return {
        address: catalogItem?.[addressKey] || null,
        benefits: catalogItem?.[benefitsKey] || null,
        bookingPolicy: catalogItem?.[bookingPolicyKey] || null,
        description: catalogItem?.[descriptionKey] || null,
        eventDates: catalogItem?.[eventDatesKey] || null,
        headline: catalogItem?.[headlineKey] || null,
        localInsights: catalogItem?.[localInsightsKey] || null,
        name: catalogItem?.[nameKey] || null,
        travelItineraryNotes: catalogItem?.[travelItineraryNotesKey] || null,
    };
}

export function googlePlaceToPartialCatalogLocationFilter(
    googlePlace: GooglePlace,
): Partial<SearchCatalogItemsQuery> {
    const addressFields = addressComponentsToObject(
        googlePlaceToAddressComponents(googlePlace),
    );

    return addressFieldsAndLocationToFilter(addressFields, {
        latitude: googlePlace.location?.latitude,
        longitude: googlePlace.location?.longitude,
    });
}

export function addressFieldsAndLocationToFilter(
    addressFields: AddressFields | undefined,
    locationPoint: { latitude?: number; longitude?: number },
): Partial<SearchCatalogItemsQuery> {
    if (!addressFields) {
        return {};
    }

    const countryFilter = addressFields.country
        ? { country: addressFields.country }
        : {};

    if (
        moreStrictLocationSearchThanCountry(addressFields) &&
        locationPoint.latitude &&
        locationPoint.longitude
    ) {
        return {
            ...countryFilter,
            latitude: locationPoint.latitude,
            longitude: locationPoint.longitude,
            maxGeoDistanceKm: 100,
        };
    }

    return countryFilter;
}

export function clearLocationFields(
    filter: SearchCatalogItemsQuery,
): SearchCatalogItemsQuery {
    const {
        latitude,
        longitude,
        maxGeoDistanceKm,
        geoBoundingBoxBottom,
        geoBoundingBoxTop,
        geoBoundingBoxLeft,
        geoBoundingBoxRight,
        country,
        ...rest
    } = { ...filter };
    return rest;
}
